import {useEffect, useEffectEvent, useMemo} from 'react'
import {graphql, useFragment, useLazyLoadQuery} from 'react-relay'
import {useParams, useSearchParams} from 'react-router'

import {useAppSelector} from '../../../../hooks/react-redux'
import {useQueryParam} from '../../../../hooks/routes'
import {BuildStatusType} from '../../../../types'
import {
  isPipelineCompatibilityAgentsEnabled,
  isPipelineOverviewEnabled,
} from '../../../../utils/featureToggles'
import {getDebug, getOriginalPipelineSettings} from '../EditPipelinePage/EditPipelinePage.selectors'
import {isSelfHostedRunsOn} from '../EditPipelinePage/EditPipelinePage.utils'
import {useJobIdState, usePipelineId, usePipelineScope} from '../hooks/pipeline'
import usePermissionCanEditPipeline from '../hooks/usePermissionCanEditPipeline'
import {pipelinesApi} from '../services/pipelinesApi'

import type {
  PipelineRunPageJobAgentCompatibilityStatusFragment$data,
  PipelineRunPageJobAgentCompatibilityStatusFragment$key,
} from './__generated__/PipelineRunPageJobAgentCompatibilityStatusFragment.graphql'
import type {PipelineRunPageReusedJobQuery} from './__generated__/PipelineRunPageReusedJobQuery.graphql'

import type {Build, GetBuildApiArg} from 'src/services/rest'
import {restApi} from 'src/services/rest'
import {emptyArray} from 'src/utils/empty'
import {subscribeOnBuildTypeEvents, subscribeOnProjectEvents} from 'src/utils/subscriber'
import {
  BUILD_CHANGES_LOADED,
  BUILD_FINISHED,
  BUILD_INTERRUPTED,
  BUILD_PROMOTION_SETTINGS_FINALIZED,
  BUILD_STARTED,
  BUILD_TYPE_REMOVED_FROM_QUEUE,
} from 'src/utils/subscriptionEvents'

const getPipelineRunArgs = (pipelineRunId: string, options?: {withSnapshotAgents: boolean}) => ({
  buildLocator: `id:${pipelineRunId}`,
  fields:
    `id,number,state,branchName,defaultBranch,status,statusText,canceledInfo(text,user(id,name,username))` +
    ',pinned,pinInfo(text,timestamp,user(id,name,username))' +
    `,comment(text,timestamp,user(id,name,username))` +
    ',tags(tag(name,private),$locator(private:any,owner:current))' +
    `,failedToStart,personal,detachedFromAgent,waitReason,startEstimate,user(id)` +
    `,triggered(user(username,name,avatars(urlToSize20,urlToSize40)),displayText,date)` +
    `,queuedDate,startDate,finishDate,buildType(id,internalId,projectId,project(id,internalId))` +
    `,running-info(percentageComplete,elapsedSeconds,estimatedTotalSeconds,leftSeconds,overtime)` +
    `,snapshot-dependencies(build(` +
    `attributes($locator(name:teamcity.pipeline.job.id),entry(name,value)),${
      options?.withSnapshotAgents
        ? 'agent(name,id,links(link(type,relativeUrl)),environment(osType),typeId,connected,pool(id,name)),'
        : ''
    }id,state,status,statusText,number,canceledInfo,failedToStart,personal,detachedFromAgent` +
    `,parallelized,waitReason,startEstimate,startDate,finishDate,user(id)` +
    `,buildType(name,id,internalId,project(internalId,id,name,holdsPipeline,parentProjectId,ancestorProjects(project(id,parentProjectId))))` +
    `,running-info(percentageComplete,leftSeconds),artifacts(count)` +
    `,pipelineRunJob(id)` +
    `,snapshot-dependencies(build(id,running-info(percentageComplete),buildType(id,internalId,project(internalId))))` +
    `))`,
  withBuildTypes: true,
})

export const usePipelineRunArg = (): GetBuildApiArg => {
  const {pipelineRunId = ''} = useParams()
  return useMemo(() => getPipelineRunArgs(pipelineRunId), [pipelineRunId])
}

export const usePipelineDebugArg = (): GetBuildApiArg => {
  const {pipelineId, branch} = usePipelineScope()
  const pipelineRunId = useAppSelector(state => getDebug(state, pipelineId, branch)?.runId)
  return useMemo(
    () =>
      getPipelineRunArgs(pipelineRunId ? String(pipelineRunId) : '', {withSnapshotAgents: true}),
    [pipelineRunId],
  )
}
export const usePipelineJobArg = (): GetBuildApiArg => {
  const jobId = useQueryParam('job') ?? ''

  return useMemo(
    () => ({
      buildLocator: `id:${jobId}`,
      fields:
        'id,number,state,branchName,defaultBranch,status,statusText,canceledInfo' +
        ',failedToStart,personal,detachedFromAgent,waitReason,startEstimate,user(id)' +
        ',queuedDate,startDate,finishDate,buildType(id,internalId,project(id))' +
        ',running-info(percentageComplete,elapsedSeconds,estimatedTotalSeconds,leftSeconds,overtime)' +
        ',snapshot-dependencies(build(' +
        'attributes($locator(name:teamcity.pipeline.job.id),entry(name,value)),' +
        'id,state,status,statusText,number,canceledInfo,failedToStart,personal,detachedFromAgent' +
        ',parallelized,waitReason,startEstimate,startDate,finishDate,user(id),buildType(name,project(internalId),internalId)' +
        ',running-info(percentageComplete,leftSeconds),artifacts(count)' +
        `,snapshot-dependencies(build(id,running-info(percentageComplete),buildType(project(internalId))))` +
        '))',
    }),
    [jobId],
  )
}

const reusedJobQuery = graphql`
  query PipelineRunPageReusedJobQuery($buildLocator: String!, $skip: Boolean!) {
    build(buildLocator: $buildLocator) @skip(if: $skip) {
      id
    }
  }
`

export const usePipelineRunPage = () => {
  const pipelineId = usePipelineId()
  const arg = usePipelineRunArg()
  const [searchParams] = useSearchParams()
  const {canViewPipelineSettings, canEditPipeline} = usePermissionCanEditPipeline()
  const {buildLoading, jobs, buildError, headInternalId, refetch} =
    restApi.endpoints.getBuildNormalized.useQuery(arg, {
      selectFromResult: ({data, isLoading, isUninitialized, error}) => {
        const run = data?.entities?.builds?.[data?.result!]
        const headId = run?.buildType
        const runSnapshotDependenciesBuild = run?.['snapshot-dependencies']?.build?.filter(
          ({pipelineRunJob}) => pipelineRunJob?.id,
        )
        return {
          buildLoading: isLoading || isUninitialized,
          jobs: runSnapshotDependenciesBuild ?? (emptyArray as Build[]),
          buildError: error,
          headInternalId: data?.entities?.buildTypes?.[headId!]?.internalId,
        }
      },
    })
  const {pipelineLoading, pipelineError} = pipelinesApi.endpoints.getPipelineById.useQuery(
    {id: pipelineId},
    {
      selectFromResult: ({isLoading, error, isUninitialized}) => ({
        pipelineLoading: isLoading || isUninitialized,
        pipelineError: error,
      }),
    },
  )
  const [firstJob] = jobs
  const firstJobId = firstJob?.id
  const childInternalId = firstJob?.buildType?.project?.internalId
  const [jobId, setJobId] = useJobIdState()
  const selectedJob = jobs.find(dep => String(dep.id) === jobId)
  const isEdit = searchParams.get('edit') === 'true'
  const reusedJobData = useLazyLoadQuery<PipelineRunPageReusedJobQuery>(reusedJobQuery, {
    buildLocator: `id:${jobId}`,
    skip: isEdit || jobId == null || jobs.length === 0 || selectedJob != null,
  })
  const reusedJobId = reusedJobData.build?.id

  const setInitialJobId = useEffectEvent(() => {
    if (!jobId && firstJobId && !isPipelineOverviewEnabled) {
      setJobId(String(firstJobId))
    }
  })
  useEffect(() => setInitialJobId(), [firstJobId])

  useEffect(() => {
    if (reusedJobId != null) {
      setJobId(String(reusedJobId))
    }
  }, [reusedJobId, setJobId])

  useEffect(
    () =>
      childInternalId
        ? subscribeOnProjectEvents(
            childInternalId,
            [
              BUILD_TYPE_REMOVED_FROM_QUEUE,
              BUILD_STARTED,
              BUILD_FINISHED,
              BUILD_INTERRUPTED,
              BUILD_PROMOTION_SETTINGS_FINALIZED,
            ],
            refetch,
          )
        : undefined,
    [childInternalId, refetch],
  )

  useEffect(
    () =>
      headInternalId
        ? subscribeOnBuildTypeEvents(
            headInternalId,
            [
              BUILD_CHANGES_LOADED,
              BUILD_TYPE_REMOVED_FROM_QUEUE,
              BUILD_STARTED,
              BUILD_FINISHED,
              BUILD_INTERRUPTED,
              BUILD_PROMOTION_SETTINGS_FINALIZED,
            ],
            refetch,
          )
        : undefined,
    [headInternalId, refetch],
  )

  return {
    jobId,
    buildError,
    pipelineError,
    isPageDataLoading: buildLoading || pipelineLoading,
    canViewPipelineSettings,
    canEditPipeline,
    isEdit,
  }
}

export function usePipelineRunSettingJobIdMap() {
  const arg = usePipelineRunArg()
  const {jobs} = restApi.endpoints.getBuildNormalized.useQuery(arg, {
    selectFromResult: ({data}) => {
      const {entities, result} = data ?? {}
      const {builds} = entities ?? {}
      const run = builds?.[result!]
      const jobs = run?.['snapshot-dependencies']?.build
      return {
        jobs,
      }
    },
  })

  return useMemo(
    () =>
      jobs?.reduce((acc, build) => {
        const {id, buildType} = build
        const buildTypeName = buildType?.name
        const jobIdValue = build.pipelineRunJob?.id
        const jobId = isNaN(Number(jobIdValue)) ? jobIdValue : buildTypeName

        acc.set(id, jobId)

        return acc
      }, new Map<number | null | undefined, string | undefined>()),
    [jobs],
  )
}

export function usePipelineRunJobAgentCompatibilityStatus(
  currentBuild: Build | PipelineRunPageJobAgentCompatibilityStatusFragment$data | null | undefined,
) {
  const {pipelineId, branch} = usePipelineScope()
  const runId = currentBuild?.id
  const waitReason = currentBuild?.waitReason
  const pipelineSettingJobId = useMemo(
    () =>
      currentBuild?.attributes?.entry?.find(entry => entry?.name === 'teamcity.pipeline.job.id')
        ?.value ?? '',
    [currentBuild],
  )
  const currentJobAgentId = useAppSelector(getOriginalPipelineSettings(pipelineId, branch))?.jobs?.[
    pipelineSettingJobId
  ]?.['runs-on']
  const isJobQueued = currentBuild?.state === BuildStatusType.QUEUED
  const [
    getCompatibilityAgentsQueuedJob,
    {
      compatibilityAgentsQueuedJob = [],
      compatibilityAgentsQueuedJobReady,
      isCompatibilityAgentsQueuedJobLoading,
    },
  ] = pipelinesApi.endpoints.getCompatibilityAgentsQueuedJob.useLazyQuery({
    skipPollingIfUnfocused: true,
    selectFromResult: ({data, isLoading}) => ({
      compatibilityAgentsQueuedJob: data?.compatibilities,
      compatibilityAgentsQueuedJobReady: data != null,
      isCompatibilityAgentsQueuedJobLoading: isLoading,
    }),
  })

  const compatibilityAgentQueuedJob = useMemo(() => {
    if (!isJobQueued) {
      return []
    }

    const isCurrentAgentSelfHosted = isSelfHostedRunsOn(currentJobAgentId)

    if (isCurrentAgentSelfHosted) {
      return compatibilityAgentsQueuedJob
    }
    const currentAgent = compatibilityAgentsQueuedJob.find(
      ({agent}) => agent.name === currentJobAgentId,
    )
    return currentAgent ? [currentAgent] : []
  }, [compatibilityAgentsQueuedJob, currentJobAgentId, isJobQueued])

  const isCurrentJobIncompatibilityAgent = useMemo(() => {
    if (!isJobQueued || !compatibilityAgentsQueuedJobReady) {
      return false
    }
    if (compatibilityAgentQueuedJob.length > 0) {
      return compatibilityAgentQueuedJob.every(agent => agent?.compatible === false)
    }
    // Self-hosted pool with no agents at all (TW-97707).
    // For cloud images, an empty list just means the instance is still being provisioned —
    // do not mark it as incompatible.
    return isSelfHostedRunsOn(currentJobAgentId)
  }, [
    compatibilityAgentsQueuedJobReady,
    compatibilityAgentQueuedJob,
    currentJobAgentId,
    isJobQueued,
  ])

  useEffect(() => {
    if (isJobQueued && runId != null && isPipelineCompatibilityAgentsEnabled) {
      getCompatibilityAgentsQueuedJob({buildId: runId, pipelineId})
    }
  }, [getCompatibilityAgentsQueuedJob, isJobQueued, pipelineId, runId, waitReason])

  return {
    pipelineSettingJobId,
    isCurrentJobIncompatibilityAgent,
    isCompatibilityAgentsQueuedJobLoading,
    compatibilityAgentsQueuedJob,
    compatibilityAgentQueuedJob,
  }
}

const pipelineRunJobAgentCompatibilityStatusFragment = graphql`
  fragment PipelineRunPageJobAgentCompatibilityStatusFragment on Build {
    id
    waitReason
    attributes(locator: "name:teamcity.pipeline.job.id") {
      entry {
        name
        value
      }
    }
    state
    pipelineRunJob {
      id
    }
  }
`
export function usePipelineRunJobAgentCompatibilityStatusRelay(
  key: PipelineRunPageJobAgentCompatibilityStatusFragment$key | null,
) {
  const data = useFragment(pipelineRunJobAgentCompatibilityStatusFragment, key)
  return usePipelineRunJobAgentCompatibilityStatus(data)
}
